import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  writeBatch,
  onSnapshot,
  Unsubscribe,
  type UpdateData
} from 'firebase/firestore';
import { getFirebaseAuth, getFirebaseFirestore } from '../firebase';
import { auditService, computeDiff } from './auditService';
import { logger } from '../logger';

// Result shape returned by the refreshMitcRecord Cloud Function.
// Mirrors functions/src/mitcRefresh.ts → RefreshResult.
/** One saved, reviewable proposal. Preparation costs one model call. */
export interface MitcPreparationResult {
  preparationId: string;
  status: 'prepared' | 'rejected' | 'committed';
  sourceSnapshot: { sourceUrl: string; sourceText: string };
  proposal: { category: string[]; features: string[] } | null;
  rejectedDetail?: unknown[];
}

export interface MitcCommitResult {
  mode: 'commit';
  id: string;
  alreadyCommitted: boolean;
}

export interface MitcReviewEvidence {
  feeMitc: { title: string; url: string; documentDate: string };
  rewardTerms: { title: string; url: string; documentDate: string };
}

export interface MitcRefreshResult {
  success:          boolean;
  recordId:         string;
  /** URL the function actually scraped — may differ from the record's
   *  prior sourceUrl when URL self-healing kicked in. */
  sourceUrl:        string;
  changes:          Record<string, { before: unknown; after: unknown }>;
  notExtracted:     string[];
  inputTextLength:  number;
  sourceFormat:     'html' | 'pdf' | 'unknown';
  durationMs:       number;
  blockedWrites?: Array<{ field: string; kind: string; detail: string; before: unknown; after: unknown }>;
  evidencePath?: string;
  evidenceError?: string;
  error?:           string;
}

export class MitcRefreshError extends Error {
  constructor(message: string, public readonly result?: MitcRefreshResult) {
    super(message)
  }
}

/**
 * Card lifecycle state.
 *
 * ⚠️ THREE VALUES, NOT TWO. `uncertain` means the detector could not confirm
 * the card is still issued — a page it could not read, or a source with no
 * standing to answer. It is NOT a weaker 'active': rendering it identically to
 * available collapses it back into a boolean, which is the defect this exists
 * to remove.
 *
 * ABSENT is a fourth situation and is NOT the same as `uncertain`: absent
 * means never determined (wants a refresh), `uncertain` means determined that
 * we cannot tell (wants research). Absent is defaulted AT READ and must never
 * be written.
 */
export type CardStatus = 'active' | 'discontinued' | 'uncertain';

export interface MitcRecord {
  id: string;
  cardName: string;
  bankName: string;
  joiningFee: string;
  annualFee: string;
  annualFeeWaiverOnSpend: string; // <-- new
  interestRate: string;
  latePaymentCharges: string;
  foreignTransactionFee: string;
  rewardRate: string;
  cashbackRate: string;
  fuelSurchargeWaiver: string;
  interestFreePeriod: string;
  addOnCardFee: string;
  cashWithdrawalFee: string;
  overLimitFee: string;
  duplicateStatementFee: string;
  cardReplacementFee: string;
  emiConversionCharges: string;
  gstApplicable: string;
  sourceUrl: string;
  imageUrl: string;
  paymentNetwork?: string; // <-- new
  createdAt: Date;
  updatedAt: Date;
  uploadSessionId?: string;
  // Card lifecycle — set by the refresh pipeline. Absent means 'active'.
  cardStatus?: CardStatus;
  lastRefreshedAt?: Date;
  lastRefreshFailureAt?: Date;
  // Admin referral link (prioritized over sourceUrl)
  referralLink?: {
    url: string;
    notes?: string;
    addedBy?: string;
    addedAt?: Date;
  };
}

export interface MitcUploadSession {
  id: string;
  fileName: string;
  fileSize: number;
  recordsCount: number;
  uploadedAt: Date;
  uploadedBy: string;
  jsonData?: string; // Store the actual JSON content
  integrationResult?: {
    totalCards: number;
    matchedCards: number;
    updatedCards: number;
    unmatchedMitc: number;
    errors: string[];
  };
}

export interface MitcJsonFile {
  id: string;
  fileName: string;
  fileSize: number;
  jsonContent: string;
  uploadedAt: Date;
  uploadedBy: string;
  sessionId: string; // Reference to the upload session
}

export function mapJsonToMitcRecord(json: any): Omit<MitcRecord, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    cardName: json.card_name || '',
    bankName: json.bank_name || '',
    joiningFee: String(json.joining_fee_inr ?? ''),
    annualFee: String(json.annual_fee_inr ?? ''),
    annualFeeWaiverOnSpend: String(json.annual_fee_waiver_on_spend_inr ?? ''), // <-- new
    interestRate: String(json.apr_percent ?? ''),
    latePaymentCharges: json.late_payment_charges || '',
    foreignTransactionFee: String(json.foreign_transaction_fee_percent ?? ''),
    rewardRate: json.reward_rate || '',
    cashbackRate: json.cashback_rate || '',
    fuelSurchargeWaiver: json.fuel_surcharge_waiver || '',
    interestFreePeriod: String(json.interest_free_period_days ?? ''),
    addOnCardFee: String(json.add_on_card_fee_inr ?? ''),
    cashWithdrawalFee: json.cash_withdrawal_fee || '',
    overLimitFee: json.over_limit_fee || '',
    duplicateStatementFee: String(json.duplicate_statement_fee_inr ?? json.duplicate_statement_fee ?? ''),
    cardReplacementFee: String(json.card_replacement_fee_inr ?? json.card_replacement_fee ?? ''),
    emiConversionCharges: json.emi_conversion_charges || '',
    gstApplicable: String(json.gst_percent ?? ''),
    imageUrl: json.image_url || '',
    sourceUrl: json.source_url || '',
    paymentNetwork: json.payment_network || '', // <-- new
    uploadSessionId: json.uploadSessionId || '',
  };
}

const COLLECTION_NAME = 'mitc_records';
const UPLOAD_SESSIONS_COLLECTION = 'mitc_upload_sessions';
const JSON_FILES_COLLECTION = 'mitc_json_files';

class MitcService {
  private db = getFirebaseFirestore();

  // Get single MITC record
  async getMitcRecord(recordId: string): Promise<MitcRecord | null> {
    try {
      const docRef = doc(this.db, COLLECTION_NAME, recordId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return { 
          id: docSnap.id, 
          ...data,
          imageUrl: data.imageUrl || '',
          createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt) || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt) || new Date()
        } as MitcRecord;
      }
      return null;
    } catch (error) {
      logger.error('Error getting MITC record:', error);
      throw error;
    }
  }

  // Get all MITC records with pagination
  async getMitcRecords(
    filters: {
      bankName?: string;
      cardName?: string;
    } = {},
    pageSize: number = 20,
    lastDoc?: QueryDocumentSnapshot<DocumentData>
  ): Promise<{ data: MitcRecord[]; lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
    try {
      const constraints: Array<any> = [];
      
      if (filters.bankName) {
        constraints.push(where('bankName', '==', filters.bankName));
      }
      if (filters.cardName) {
        constraints.push(where('cardName', '==', filters.cardName));
      }
      
      constraints.push(orderBy('createdAt', 'desc'));
      
      let q = query(collection(this.db, COLLECTION_NAME), ...constraints);
      
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      q = query(q, limit(pageSize));
      const querySnapshot = await getDocs(q);
      
      const data = querySnapshot.docs.map(doc => {
        const docData = doc.data();
        return {
          id: doc.id,
          ...docData,
          imageUrl: docData.imageUrl || '',
          createdAt: docData.createdAt?.toDate?.() || new Date(docData.createdAt) || new Date(),
          updatedAt: docData.updatedAt?.toDate?.() || new Date(docData.updatedAt) || new Date()
        } as MitcRecord;
      });
      
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
      
      return { data, lastDoc: lastVisible };
    } catch (error) {
      logger.error('Error getting MITC records:', error);
      throw error;
    }
  }

  // Add new MITC record
  async addMitcRecord(recordData: Omit<MitcRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(this.db, COLLECTION_NAME), {
        ...recordData,
        imageUrl: recordData.imageUrl || '',
        createdAt: new Date(),
        updatedAt: new Date()
      });
      await auditService.writeAudit({
        action:      'create',
        entityType:  'mitc',
        entityId:    docRef.id,
        entityLabel: mitcLabel(recordData),
      });
      return docRef.id;
    } catch (error) {
      logger.error('Error adding MITC record:', error);
      throw error;
    }
  }

  // Update MITC record
  async updateMitcRecord(recordId: string, recordData: UpdateData<MitcRecord>): Promise<void> {
    try {
      const before = await this.getMitcRecord(recordId);
      const docRef = doc(this.db, COLLECTION_NAME, recordId);
      await updateDoc(docRef, {
        ...recordData,
        updatedAt: new Date()
      });
      await auditService.writeAudit({
        action:      'update',
        entityType:  'mitc',
        entityId:    recordId,
        entityLabel: mitcLabel(before),
        changes:     computeDiff(
          (before ?? {}) as unknown as Record<string, unknown>,
          recordData as unknown as Record<string, unknown>,
        ),
      });
    } catch (error) {
      logger.error('Error updating MITC record:', error);
      throw error;
    }
  }

  // Delete MITC record
  async deleteMitcRecord(recordId: string): Promise<void> {
    try {
      const before = await this.getMitcRecord(recordId);
      const docRef = doc(this.db, COLLECTION_NAME, recordId);
      await deleteDoc(docRef);
      await auditService.writeAudit({
        action:      'delete',
        entityType:  'mitc',
        entityId:    recordId,
        entityLabel: mitcLabel(before),
      });
    } catch (error) {
      logger.error('Error deleting MITC record:', error);
      throw error;
    }
  }

  // Check for duplicate MITC records
  async checkForDuplicates(records: Omit<MitcRecord, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<{
    duplicates: Array<{ record: Omit<MitcRecord, 'id' | 'createdAt' | 'updatedAt'>; existingId: string }>;
    newRecords: Omit<MitcRecord, 'id' | 'createdAt' | 'updatedAt'>[];
  }> {
    try {
      const existingRecords = await this.getMitcRecords({}, 1000);
      const duplicates: Array<{ record: Omit<MitcRecord, 'id' | 'createdAt' | 'updatedAt'>; existingId: string }> = [];
      const newRecords: Omit<MitcRecord, 'id' | 'createdAt' | 'updatedAt'>[] = [];

      for (const newRecord of records) {
        // Normalize the data for comparison
        const normalizedNewCardName = newRecord.cardName.toLowerCase().trim().replace(/\s+/g, ' ');
        const normalizedNewBankName = newRecord.bankName.toLowerCase().trim().replace(/\s+/g, ' ');
        
        const isDuplicate = existingRecords.data.find(existing => {
          const normalizedExistingCardName = existing.cardName.toLowerCase().trim().replace(/\s+/g, ' ');
          const normalizedExistingBankName = existing.bankName.toLowerCase().trim().replace(/\s+/g, ' ');
          
          // Exact match
          if (normalizedNewCardName === normalizedExistingCardName && 
              normalizedNewBankName === normalizedExistingBankName) {
            return true;
          }
          
          // Check for common variations
          const cardNameVariations = [
            normalizedNewCardName,
            normalizedNewCardName.replace('credit card', '').trim(),
            normalizedNewCardName.replace('card', '').trim(),
            normalizedNewCardName.replace('bank', '').trim()
          ];
          
          const bankNameVariations = [
            normalizedNewBankName,
            normalizedNewBankName.replace('bank', '').trim(),
            normalizedNewBankName.replace('ltd', '').trim(),
            normalizedNewBankName.replace('limited', '').trim()
          ];
          
          return cardNameVariations.some(cardVar => 
            bankNameVariations.some(bankVar => 
              cardVar === normalizedExistingCardName && bankVar === normalizedExistingBankName
            )
          );
        });

        if (isDuplicate) {
          duplicates.push({ record: newRecord, existingId: isDuplicate.id });
        } else {
          newRecords.push(newRecord);
        }
      }

      return { duplicates, newRecords };
    } catch (error) {
      logger.error('Error checking for duplicates:', error);
      throw error;
    }
  }

  // Bulk upload MITC records with duplicate handling
  async bulkUploadMitcRecords(records: Omit<MitcRecord, 'id' | 'createdAt' | 'updatedAt'>[], sessionId?: string): Promise<{
    added: string[];
    duplicates: Array<{ record: Omit<MitcRecord, 'id' | 'createdAt' | 'updatedAt'>; existingId: string }>;
  }> {
    try {
      // Check for duplicates first
      const { duplicates, newRecords } = await this.checkForDuplicates(records);

      // Only upload new records
      if (newRecords.length === 0) {
        return { added: [], duplicates };
      }

      const batch = writeBatch(this.db);
      const docRefs: string[] = [];

      newRecords.forEach((record) => {
        const docRef = doc(collection(this.db, COLLECTION_NAME));
        batch.set(docRef, {
          ...record,
          imageUrl: record.imageUrl || '',
          uploadSessionId: sessionId, // Track which session this record belongs to
          createdAt: new Date(),
          updatedAt: new Date()
        });
        docRefs.push(docRef.id);
      });

      await batch.commit();
      await auditService.writeAudit({
        action:      'create',
        entityType:  'mitc',
        entityId:    sessionId || 'bulk',
        entityLabel: `Bulk upload (${docRefs.length} records)`,
        metadata:    {
          added:           docRefs.length,
          duplicates:      duplicates.length,
          sessionId:       sessionId ?? null,
        },
      });
      return { added: docRefs, duplicates };
    } catch (error) {
      logger.error('Error bulk uploading MITC records:', error);
      throw error;
    }
  }

  // Get MITC statistics
  async getMitcStats(): Promise<{
    totalRecords: number;
    validRecords: number;
    incompleteRecords: number;
    banksCount: number;
    lastUpdated: Date | null;
  }> {
    try {
      const querySnapshot = await getDocs(collection(this.db, COLLECTION_NAME));
      const records = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          imageUrl: data.imageUrl || '',
          createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt) || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt) || new Date()
        } as MitcRecord;
      });

      const validRecords = records.filter(r => r.cardName && r.bankName);
      const incompleteRecords = records.filter(r => !r.cardName || !r.bankName);
      const banks = new Set(records.map(r => r.bankName).filter(Boolean));
      const lastUpdated = records.length > 0 
        ? new Date(Math.max(...records.map(r => r.updatedAt.getTime())))
        : null;

      return {
        totalRecords: records.length,
        validRecords: validRecords.length,
        incompleteRecords: incompleteRecords.length,
        banksCount: banks.size,
        lastUpdated
      };
    } catch (error) {
      logger.error('Error getting MITC stats:', error);
      throw error;
    }
  }

  // Search MITC records
  async searchMitcRecords(searchTerm: string): Promise<MitcRecord[]> {
    try {
      // Note: Firestore doesn't support full-text search natively
      // This is a simple implementation - for production, consider using Algolia or similar
      const querySnapshot = await getDocs(collection(this.db, COLLECTION_NAME));
      const records = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          imageUrl: data.imageUrl || '',
          createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt) || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt) || new Date()
        } as MitcRecord;
      });

      const searchLower = searchTerm.toLowerCase();
      return records.filter(record => 
        record.cardName.toLowerCase().includes(searchLower) ||
        record.bankName.toLowerCase().includes(searchLower)
      );
    } catch (error) {
      logger.error('Error searching MITC records:', error);
      throw error;
    }
  }

  // Real-time listener for MITC records
  onMitcRecordsChange(callback: (records: MitcRecord[]) => void): Unsubscribe {
    const q = query(collection(this.db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (querySnapshot) => {
      const records = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          imageUrl: data.imageUrl || '',
          createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt) || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt) || new Date()
        } as MitcRecord;
      });
      
      callback(records);
    });
  }

  // Export all MITC data
  async exportMitcData(): Promise<MitcRecord[]> {
    try {
      const querySnapshot = await getDocs(collection(this.db, COLLECTION_NAME));
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          imageUrl: data.imageUrl || '',
          createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt) || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt) || new Date()
        } as MitcRecord;
      });
    } catch (error) {
      logger.error('Error exporting MITC data:', error);
      throw error;
    }
  }

  // Get MITC upload sessions
  async getUploadSessions(): Promise<MitcUploadSession[]> {
    try {
      const q = query(
        collection(this.db, UPLOAD_SESSIONS_COLLECTION),
        orderBy('uploadedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          fileName: data.fileName || '',
          fileSize: data.fileSize || 0,
          recordsCount: data.recordsCount || 0,
          uploadedBy: data.uploadedBy || '',
          uploadedAt: data.uploadedAt?.toDate?.() || new Date(data.uploadedAt) || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt) || new Date()
        } as MitcUploadSession;
      });
    } catch (error) {
      logger.error('Error getting upload sessions:', error);
      throw error;
    }
  }

  // Add upload session
  async addUploadSession(sessionData: Omit<MitcUploadSession, 'id'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(this.db, UPLOAD_SESSIONS_COLLECTION), {
        ...sessionData,
        uploadedAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      logger.error('Error adding upload session:', error);
      throw error;
    }
  }

  // Update upload session with integration result
  async updateUploadSession(sessionId: string, integrationResult: MitcUploadSession['integrationResult']): Promise<void> {
    try {
      const docRef = doc(this.db, UPLOAD_SESSIONS_COLLECTION, sessionId);
      await updateDoc(docRef, {
        integrationResult,
        updatedAt: new Date()
      });
    } catch (error) {
      logger.error('Error updating upload session:', error);
      throw error;
    }
  }

  // Delete upload session and associated records
  async deleteUploadSession(sessionId: string): Promise<{
    deletedSession: boolean;
    deletedRecords: number;
  }> {
    try {
      const batch = writeBatch(this.db);
      let deletedRecords = 0;

      // First, try to delete by session ID
      const recordsQuery = query(
        collection(this.db, COLLECTION_NAME),
        where('uploadSessionId', '==', sessionId)
      );
      const recordsSnapshot = await getDocs(recordsQuery);
      
      recordsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
        deletedRecords++;
      });

      // Delete the upload session
      const sessionRef = doc(this.db, UPLOAD_SESSIONS_COLLECTION, sessionId);
      batch.delete(sessionRef);

      // Delete the JSON file
      const jsonFileQuery = query(
        collection(this.db, JSON_FILES_COLLECTION),
        where('sessionId', '==', sessionId)
      );
      const jsonFileSnapshot = await getDocs(jsonFileQuery);
      
      jsonFileSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();

      await auditService.writeAudit({
        action:      'delete',
        entityType:  'mitc_session',
        entityId:    sessionId,
        metadata:    { deletedRecords },
      });

      return {
        deletedSession: true,
        deletedRecords
      };
    } catch (error) {
      logger.error('Error deleting upload session:', error);
      throw error;
    }
  }

  // Save JSON file to database
  async saveJsonFile(fileName: string, fileSize: number, jsonContent: string, sessionId: string, uploadedBy: string = 'admin'): Promise<string> {
    try {
      const docRef = await addDoc(collection(this.db, JSON_FILES_COLLECTION), {
        fileName,
        fileSize,
        jsonContent,
        uploadedAt: new Date(),
        uploadedBy,
        sessionId
      });
      return docRef.id;
    } catch (error) {
      logger.error('Error saving JSON file:', error);
      throw error;
    }
  }

  // Get JSON file by session ID
  async getJsonFileBySessionId(sessionId: string): Promise<MitcJsonFile | null> {
    try {
      const q = query(
        collection(this.db, JSON_FILES_COLLECTION),
        where('sessionId', '==', sessionId)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          uploadedAt: data.uploadedAt?.toDate?.() || new Date(data.uploadedAt) || new Date()
        } as MitcJsonFile;
      }
      return null;
    } catch (error) {
      logger.error('Error getting JSON file:', error);
      throw error;
    }
  }

  // Delete JSON file
  async deleteJsonFile(fileId: string): Promise<void> {
    try {
      const docRef = doc(this.db, JSON_FILES_COLLECTION, fileId);
      await deleteDoc(docRef);
    } catch (error) {
      logger.error('Error deleting JSON file:', error);
      throw error;
    }
  }

  // Get all JSON files
  async getJsonFiles(): Promise<MitcJsonFile[]> {
    try {
      const q = query(
        collection(this.db, JSON_FILES_COLLECTION),
        orderBy('uploadedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          uploadedAt: data.uploadedAt?.toDate?.() || new Date(data.uploadedAt) || new Date()
        } as MitcJsonFile;
      });
    } catch (error) {
      logger.error('Error getting JSON files:', error);
      throw error;
    }
  }
}

function mitcLabel(record: { cardName?: string; bankName?: string } | null | undefined): string | undefined {
  if (!record) return undefined
  const parts = [record.cardName, record.bankName].filter(Boolean)
  return parts.length ? parts.join(' • ') : undefined
}

/**
 * Call the refreshMitcRecord Cloud Function for a single record.
 * The function fetches the record's sourceUrl, extracts MITC fields
 * via GPT-4o-mini, diffs against the current record, and applies
 * non-null changes with an audit_log entry — see
 * functions/src/mitcRefresh.ts.
 */
export async function refreshMitcRecordFromSource(
  recordId: string,
): Promise<MitcRefreshResult> {
  const auth = getFirebaseAuth()
  const user = auth.currentUser
  if (!user) {
    throw new Error('Sign in first — refresh requires an admin token.')
  }
  const token = await user.getIdToken()

  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID
  if (!projectId) {
    throw new Error('VITE_FIREBASE_PROJECT_ID is not set.')
  }
  const url =
    `https://us-central1-${projectId}.cloudfunctions.net/refreshMitcRecord`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type':  'application/json',
    },
    body: JSON.stringify({ recordId }),
  })

  const result = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new MitcRefreshError(
      result?.error || `Refresh failed with HTTP ${response.status}`,
      typeof result?.success === 'boolean' && result?.recordId === recordId && result?.changes
        ? result as MitcRefreshResult : undefined,
    )
  }
  return result as MitcRefreshResult
}

/** One record awaiting re-enrichment. */
export interface EnrichmentQueueItem {
  id: string;
  cardName: string;
  clearedAt: Date | null;
  ageDays: number | null;
}

/**
 * THE ENRICHMENT QUEUE, computed the same way the ops alert computes it:
 *
 *   enrichmentClearedAt != null  AND  NOT (category.length > 0 OR features.length > 0)
 *
 * ⚠️ THE ACTION DERIVES ITS OWN IDS FROM THIS. It is deliberately NOT driven by
 * the table's selection or filters: the endpoint regenerates ALL 96 records
 * when called without ids, and regeneration is NONDETERMINISTIC, so a bare or
 * mis-scoped run silently replaces every validated label set to repair a
 * handful. Deriving the ids here means the dangerous mode is not reachable
 * from the panel at all — a guard by construction rather than by instruction.
 *
 * ⚠️ KNOWN GAP, MATCHING THE ALERT'S: a record that was NEVER ENRICHED has no
 * `enrichmentClearedAt`, so it cannot appear in a queue keyed on having been
 * CLEARED. Axis Atlas is exactly that — created by the coverage pilot and
 * never enriched. Reproduced here on purpose so the button and the alert agree;
 * fixing the predicate is tracked separately.
 */
export async function getEnrichmentQueue(): Promise<EnrichmentQueueItem[]> {
  const db = getFirebaseFirestore()
  const snap = await getDocs(collection(db, COLLECTION_NAME))
  const now = Date.now()
  const out: EnrichmentQueueItem[] = []
  snap.forEach((d) => {
    const x = d.data() as Record<string, unknown>
    const cleared = x.enrichmentClearedAt as { toDate?: () => Date } | undefined
    if (!cleared?.toDate) return
    const cat = Array.isArray(x.category) ? x.category.length : 0
    const feat = Array.isArray(x.features) ? x.features.length : 0
    if (cat > 0 || feat > 0) return
    const at = cleared.toDate()
    out.push({
      id: d.id,
      cardName: String(x.cardName ?? d.id),
      clearedAt: at,
      ageDays: Math.floor((now - at.getTime()) / 86400000),
    })
  })
  return out.sort((a, b) => (b.ageDays ?? 0) - (a.ageDays ?? 0))
}

/** Admin-authenticated request to the one-card enrichment endpoint. */
async function requestMitcEnrichment<T>(
  method: 'PUT' | 'PATCH', params: URLSearchParams,
  body?: Record<string, unknown>,
): Promise<T> {
  const auth = getFirebaseAuth()
  const user = auth.currentUser
  if (!user) {
    throw new Error('Sign in first — re-enrichment requires an admin token.')
  }
  const token = await user.getIdToken()

  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID
  if (!projectId) {
    throw new Error('VITE_FIREBASE_PROJECT_ID is not set.')
  }
  const url =
    `https://us-central1-${projectId}.cloudfunctions.net/` +
    `backfillMitcEnrichment?${params.toString()}`

  const response = await fetch(url, {
    method,
    headers: { 'Authorization': `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })

  const result = await response.json().catch(() => ({}))
  if (!response.ok) {
    // Unknown ids come back as 400 with the offenders named — surface that
    // rather than a bare status, so a typo is legible instead of looking
    // like a silently short run.
    throw new Error(
      result?.error
        ? `${result.error}${result.missing ? `: ${result.missing.join(', ')}` : ''}`
        : `Re-enrichment failed with HTTP ${response.status}`,
    )
  }
  return result as T
}

/** A stable key lets a timed-out preparation retry return the saved result. */
export function prepareMitcEnrichment(
  id: string, prepareKey: string,
): Promise<MitcPreparationResult> {
  if (!id || id.includes(',') || id.includes('/')) {
    throw new Error('Choose one exact MITC record ID.')
  }
  return requestMitcEnrichment('PUT', new URLSearchParams({
    ids: id, prepare: 'true', prepareKey,
  }))
}

/** Commit uses the reviewed preparation; it makes no new model call. */
export function commitMitcEnrichment(
  preparationId: string, reviewEvidence: MitcReviewEvidence,
): Promise<MitcCommitResult> {
  if (!/^[a-f0-9]{64}$/.test(preparationId)) {
    throw new Error('Invalid enrichment preparation ID.')
  }
  return requestMitcEnrichment('PATCH',
    new URLSearchParams({commit: preparationId}), {reviewEvidence})
}

export const mitcService = new MitcService();
